import React from 'react';
import { Link } from 'react-router-dom';
import { Search, Globe, Calculator, BookOpen, HelpCircle, ArrowRight } from 'lucide-react';
import Header from '../atoms/Header';
import Footer from '../atoms/Footer';
import Contact from '../atoms/Contact';
import Faq from '../atoms/Faq';
import Typography from '../components/v2/Typography';
import Button from '../components/v2/Button';
import '../scss/resources.scss';
import ServicesHeader from '../atoms/ServicesHeader';
import ResourcesHeader from '../atoms/ResourcesHeader';
import BlogSection from '../atoms/BlogPreview';

const resourcesFaqs = [
    {
        id: 1,
        q: 'What is domain brokerage?',
        a: 'Domain brokerage involves professional intermediaries who facilitate the buying and selling of domain names. Brokers leverage their expertise, network, and negotiation skills to help clients acquire premium domains or sell their existing domains at optimal prices.'
    },
    {
        id: 2,
        q: 'How do I know if a domain is available?',
        a: 'You can check domain availability using WHOIS lookup tools. If a domain is registered, the WHOIS data will show ownership information (unless private). Our WHOIS tool provides comprehensive information about domain registration status, expiration dates, and registrar details.'
    },
    {
        id: 3,
        q: 'What factors determine domain value?',
        a: 'Domain value depends on multiple factors including length (shorter is better), keywords, brandability, extension (.com is most valuable), search volume, commercial potential, and comparable sales. Our appraisal tool analyzes these factors to provide accurate valuations.'
    },
    {
        id: 4,
        q: 'How long does a domain transfer take?',
        a: 'Domain transfers typically take 5-7 days to complete. The process involves unlocking the domain, obtaining the transfer authorization code, initiating the transfer with the new registrar, and confirming the transfer via email. Some TLDs may have different timelines.'
    },
    {
        id: 5,
        q: 'What is the difference between domain parking and development?',
        a: 'Domain parking means holding a domain without active content, often displaying ads. Development involves building a functional website or service on the domain. Developed domains typically have higher value due to established traffic, content, and revenue.'
    }
];

const glossaryTerms = [
    {
        term: 'Aftermarket',
        definition: 'The secondary market where previously registered domains are bought and sold between parties.'
    },
    {
        term: 'Backorder',
        definition: 'A service that attempts to register a domain name on your behalf the moment it becomes available.'
    },
    {
        term: 'ccTLD',
        definition: 'Country Code Top-Level Domain - domains specific to countries or territories (e.g., .uk, .ca, .de).'
    },
    {
        term: 'DNS',
        definition: 'Domain Name System - the internet\'s system for converting domain names into IP addresses.'
    },
    {
        term: 'Domain Authority',
        definition: 'A search engine ranking score that predicts how well a website will rank on search engine result pages.'
    },
    {
        term: 'EPP Code',
        definition: 'Extensible Provisioning Protocol code - a unique authorization code required to transfer domains between registrars.'
    },
    {
        term: 'Escrow',
        definition: 'A secure transaction method where a third party holds funds until both buyer and seller fulfill their obligations.'
    },
    {
        term: 'gTLD',
        definition: 'Generic Top-Level Domain - common domain extensions like .com, .net, .org, and newer ones like .app, .shop.'
    },
    {
        term: 'ICANN',
        definition: 'Internet Corporation for Assigned Names and Numbers - the organization that coordinates the internet\'s naming system.'
    },
    {
        term: 'Registrant',
        definition: 'The individual or organization that holds the rights to use a specific domain name.'
    },
    {
        term: 'Registrar',
        definition: 'An ICANN-accredited company authorized to register and manage domain names on behalf of registrants.'
    },
    {
        term: 'Registry',
        definition: 'The organization that maintains the database of all domain names registered in each top-level domain.'
    },
    {
        term: 'WHOIS',
        definition: 'A protocol for querying databases to obtain information about domain name registration and ownership.'
    },
    {
        term: 'Zone File',
        definition: 'A text file that contains mappings between domain names and IP addresses for a specific domain.'
    }
];

const toolsData = [
    {
        icon: <Search size={32} />,
        title: 'WHOIS Lookup',
        description: 'Check domain ownership, registration details, and availability status instantly.',
        link: '/tool/whois',
        color: '#4A90E2'
    },
    {
        icon: <Calculator size={32} />,
        title: 'Domain Appraisal',
        description: 'Get accurate domain valuations based on market data and key metrics.',
        link: '/tool/appraisal',
        color: '#50C878'
    },
    // {
    //     icon: <Globe size={32} />,
    //     title: 'Domain Search',
    //     description: 'Search and explore premium domains available in our marketplace.',
    //     link: '/featured',
    //     color: '#FF6B6B'
    // }
];

export default function ResourcesPage() {
    return (
        <>
            {/* <section className="resources-hero-wrapper">
                <div className="inner">
                    <Header isDark />
                    <div className="resources-hero-content">
                        <Typography variant="h1">Domain Resources & Tools</Typography>
                        <Typography variant="para">
                            Everything you need to know about domain names, plus powerful tools to help you make informed decisions
                        </Typography>
                    </div>
                </div>
            </section> */}
            {/* <ResourcesHeader active="/resources" /> */}

            <section className="services-hero-wrapper" style={{
                paddingBottom: 0
            }}>
            <div className="inner">
                <div className="hero-heading" style={{marginBottom: 4, textAlign: "center", justifyContent: "center"}}>
                <Typography variant="h1">Resources</Typography>
                
                </div>
                <div className="hero-heading" style={{textAlign: "center", justifyContent: "center"}}>
                <Typography variant="para">
                    Everything you need to know about domain names, plus powerful tools to help you make informed decisions
                </Typography>
                </div>
            </div>
            </section>

            <section className="resources-tools-wrapper">
                <div className="inner">
                    <div className="section-header">
                        <Typography variant="h2">Domain Tools</Typography>
                        <Typography variant="para">
                            Professional-grade tools to research, evaluate, and manage domain names
                        </Typography>
                    </div>
                    <div className="tools-grid">
                        {toolsData.map((tool, index) => (
                            <Link to={tool.link} key={index} className="tool-card">
                                <div className="tool-icon" style={{ color: tool.color }}>
                                    {tool.icon}
                                </div>
                                <Typography variant="h3">{tool.title}</Typography>
                                <Typography variant="para">{tool.description}</Typography>
                                <div className="tool-action">
                                    {/* <Typography variant="para">Access Tool</Typography>
                                    <ArrowRight size={20} /> */}
                                    <Button variant="primary" linkProps={{to: tool.link}} isLink={true}>
                                        Access Tool
                                    </Button>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <section className="resources-glossary-wrapper">
                <div className="inner">
                    <div className="section-header">
                        <BookOpen size={40} />
                        <Typography variant="h2">Domain Glossary</Typography>
                        <Typography variant="para">
                            Essential terminology every domain investor should know
                        </Typography>
                    </div>
                    <div className="glossary-grid">
                        {glossaryTerms.map((item, index) => (
                            <div key={index} className="glossary-item">
                                <Typography variant="h4">{item.term}</Typography>
                                <Typography variant="para">{item.definition}</Typography>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="resources-faq-wrapper">
                <div className="inner">
                    <div className="section-header">
                        <HelpCircle size={40} />
                        <Typography variant="h2">Frequently Asked Questions</Typography>
                        <Typography variant="para">
                            Find answers to common questions about domain names and our services
                        </Typography>
                    </div>
                    <Faq data={resourcesFaqs} showHeader={false} noBorder={true} />
                    <div className="faq-cta">
                        <Typography variant="para">Have more questions?</Typography>
                        <Button variant="primary" isLink={true} linkProps={{to: "/contact"}}>
                            Contact Us
                        </Button>
                    </div>
                </div>
            </section>

            <section className="resources-guides-wrapper">
                <div className="inner">
                    <div className="section-header">
                        <Typography variant="h2">Domain Guides & Best Practices</Typography>
                        <Typography variant="para">
                            Learn from industry experts and improve your domain strategy
                        </Typography>
                    </div>
                    <div className="guides-grid">
                        <div className="guide-card">
                            <Typography variant="h3">Buying Domains</Typography>
                            <ul>
                                <li><Typography variant="para">Research market values before making offers</Typography></li>
                                <li><Typography variant="para">Use escrow services for secure transactions</Typography></li>
                                <li><Typography variant="para">Verify domain history and backlinks</Typography></li>
                                <li><Typography variant="para">Check trademark conflicts</Typography></li>
                            </ul>
                        </div>
                        <div className="guide-card">
                            <Typography variant="h3">Selling Domains</Typography>
                            <ul>
                                <li><Typography variant="para">Get professional appraisals</Typography></li>
                                <li><Typography variant="para">List on multiple marketplaces</Typography></li>
                                <li><Typography variant="para">Optimize landing pages</Typography></li>
                                <li><Typography variant="para">Be responsive to inquiries</Typography></li>
                            </ul>
                        </div>
                        <div className="guide-card">
                            <Typography variant="h3">Domain Security</Typography>
                            <ul>
                                <li><Typography variant="para">Enable registrar lock</Typography></li>
                                <li><Typography variant="para">Use two-factor authentication</Typography></li>
                                <li><Typography variant="para">Keep contact info updated</Typography></li>
                                <li><Typography variant="para">Set renewal to auto</Typography></li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            <BlogSection display={'both'} />


            <Contact />

        </>
    );
}